import { Accordion, Button } from "flowbite-react";
import { useForm } from "react-hook-form";
import { ADMIN } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useState } from "react";
import Input from "../inputs/Input";
import DrawerLoading from "../DrawerLoading";
import CurrencyInput from "../inputs/CurrencyInput";
import { useQuery } from "react-query";
import PhoneInput from "../inputs/PhoneInput";

/**
 * ViewProviderForm
 * - Carrega provider + associatedQueries
 * - Preenche form controlado pelo RHF
 * - Envia payload normalizado:
 *   {
 *     name, responsibleName, phoneNumber,
 *     queries: [{ queryId, providerId, price }]
 *   }
 */
const ViewProviderForm = ({ provider, fetchProviders, onClose }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const {
    isLoading: isLoadingProvider,
    error: errorListProvider,
    data: providerReceived,
  } = useQuery(
    ["providerReceived", provider?.id],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_PROVIDER}/${provider?.id}`);
      return data.data;
    },
    { enabled: !!provider?.id }
  );

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      responsibleName: "",
      phoneNumber: "",
      queries: [],
    },
  });

  // Quando chegar o provider completo da API, popula o form
  useEffect(() => {
    if (!providerReceived) return;

    const queries = (providerReceived.associatedQueries || []).map((q) => ({
      name: q?.name || "",
      QueryProvider: {
        queryId: q?.QueryProvider?.queryId ?? q?.id, // << AQUI
        originalName: q?.QueryProvider?.originalName || "",
        // backend manda "0.00" (string) — convertemos p/ número
        price:
          typeof q?.QueryProvider?.price === "number"
            ? q.QueryProvider.price
            : Number.parseFloat(q?.QueryProvider?.price || "0") || 0,
      },
    }));

    reset({
      name: providerReceived.name || provider?.name || "",
      responsibleName:
        providerReceived.responsibleName || provider?.responsibleName || "",
      phoneNumber: providerReceived.phoneNumber || provider?.phoneNumber || "",
      queries,
    });
  }, [providerReceived, provider, reset]);

  // helper robusto: aceita número, "0.00", "R$ 1.234,56", etc.
  const toNumberBRL = (v) => {
    if (typeof v === "number") return v;
    if (typeof v !== "string") return null;
    let s = v.trim();
    if (/^\d+(\.\d+)?$/.test(s)) return Number.parseFloat(s); // "0.00"
    s = s
      .replace(/[^\d.,-]/g, "")
      .replace(/\./g, "")
      .replace(",", ".");
    const n = Number(s);
    return Number.isFinite(n) ? n : null;
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      setError(null);

      const queries = (data?.queries || [])
        .map((q) => {
          const qp = q?.QueryProvider || {};
          const queryId = qp?.queryId != null ? Number(qp.queryId) : null;
          const price = toNumberBRL(qp?.price);

          return {
            QueryProvider: {
              queryId, // << conforme backend
              providerId: Number(provider?.id), // << conforme backend
              price,
              originalName:
                qp?.originalName != null
                  ? String(qp.originalName).trim()
                  : null,
            },
          };
        })
        .filter((item) => Number.isFinite(item.QueryProvider.queryId));

      const payload = {
        name: (data.name || "").trim(),
        responsibleName: (data.responsibleName || "").trim(),
        phoneNumber: (data.phoneNumber || "").trim(),
        queries, // << agora no formato que o backend espera
      };

      await axios.put(`${ADMIN.GET_PROVIDER}/${provider?.id}`, payload);

      onClose?.(false);
      fetchProviders?.();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Erro ao atualizar dados.";
      setError({ message });
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Renderize a lista a partir do estado do RHF
  const queries = watch("queries") || [];

  return (
    <>
      <form className="mb-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="font-semibold mb-5">
          Provedor: {provider?.id} - {watch("name") || provider?.name}
        </div>

        <div className="mb-6 flex flex-col gap-4">
          <Input type="text" label="Provedor" register={register} name="name" />
          <Input
            type="text"
            label="Nome do responsável"
            register={register}
            name="responsibleName"
          />
          <PhoneInput label="Telefone" register={register} name="phoneNumber" />
        </div>

        <Accordion>
          <Accordion.Panel>
            <Accordion.Title>Insumos ({queries.length})</Accordion.Title>
            <Accordion.Content>
              <div className="flex flex-col gap-4">
                {queries.length > 0 ? (
                  queries.map((q, index) => (
                    <div key={q?.queryId ?? index}>
                      <div className="flex justify-between items-center gap-4">
                        <label className="flex items-center cursor-pointer gap-3">
                          <span className="text-sm font-medium text-gray-900 dark:text-gray-300">
                            {q.name || `Insumo #${index + 1}`}
                          </span>
                        </label>

                        <div className="flex gap-3 items-start">
                          {/* preço vem de queries[i].QueryProvider.price */}
                          <CurrencyInput
                            register={register}
                            name={`queries.${index}.QueryProvider.price`}
                            className="w-30"
                            label="Preço"
                            control={control}
                          />
                        </div>
                      </div>

                      <input
                        type="hidden"
                        {...register(`queries.${index}.QueryProvider.queryId`, {
                          valueAsNumber: true,
                        })}
                      />
                      <hr className="mt-4" />
                    </div>
                  ))
                ) : (
                  <div className="text-sm md:text-md text-center">
                    Não há nenhuma consulta cadastrada nesta categoria
                  </div>
                )}
              </div>
            </Accordion.Content>
          </Accordion.Panel>
        </Accordion>

        <Button
          className="w-full mt-4"
          type="submit"
          isProcessing={isSubmitting}
        >
          Atualizar dados
        </Button>

        {error && <p className="mt-3 text-sm text-red-600">{error.message}</p>}
      </form>

      {isLoadingProvider && <DrawerLoading />}
    </>
  );
};

export default ViewProviderForm;
